import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const CATEGORIES = [
  ["pro", "Performance Rights Organizations"],
  ["mechanical", "Mechanical Licensing"],
  ["sound_recording", "Sound Recording Royalties"],
  ["metadata", "Metadata Verification & Identifiers"],
  ["registration", "Identifier Registration"],
  ["artist_platforms", "Artist Platforms"],
  ["distribution", "Distribution"],
];

export default function ResourceEditModal({ open, resource, onClose, onSaved }) {
  const isNew = !resource?.id;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        title: "", url: "", description: "", category: "pro",
        display_order: 0, is_active: true,
        ...(resource || {}),
      });
      setErrors({});
    }
  }, [open, resource]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title required";
    else if (form.title.length > 100) e.title = "Max 100 chars";
    if (!form.url?.trim()) e.url = "URL required";
    else if (!/^https?:\/\//i.test(form.url.trim())) e.url = "Must start with http:// or https://";
    else if (form.url.length > 500) e.url = "Max 500 chars";
    if (!form.description?.trim()) e.description = "Description required";
    else if (form.description.length > 300) e.description = "Max 300 chars";
    if (!CATEGORIES.find(([v]) => v === form.category)) e.category = "Pick a category";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSave = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        url: form.url.trim(),
        description: form.description.trim(),
        category: form.category,
        display_order: Number(form.display_order) || 0,
        is_active: !!form.is_active,
      };
      let r;
      if (isNew) {
        r = await api.post("/admin/resources", payload);
      } else {
        r = await api.patch(`/admin/resources/${resource.id}`, payload);
      }
      toast.success("Resource saved.");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      const msg = Array.isArray(det) ? det[0]?.msg : (typeof det === "string" ? det : "Save failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="resource-modal">
      <div className="modal-card" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "22px 24px 14px", borderBottom: "1px solid var(--border-soft)" }}>
          <div className="eyebrow" style={{ color: "var(--accent)" }}>
            — {isNew ? "New Resource" : "Edit Resource"}
          </div>
          <input
            data-testid="resource-title-input"
            className="lq-input"
            placeholder="Resource title (e.g. ASCAP)"
            value={form.title || ""}
            onChange={(e) => set("title", e.target.value)}
            maxLength={100}
            style={{
              fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 26,
              border: 0, padding: "8px 0", background: "transparent",
            }}
          />
          {errors.title && <div className="data-line" style={{ color: "var(--red-light)" }}>{errors.title}</div>}
        </div>

        <div style={{ padding: "16px 24px 8px" }}>
          <div style={{ marginBottom: 14 }}>
            <label className="label">URL</label>
            <input
              data-testid="resource-url-input"
              type="url"
              className="lq-input"
              placeholder="https://..."
              value={form.url || ""}
              onChange={(e) => set("url", e.target.value)}
              maxLength={500}
            />
            {errors.url && <div className="data-line" style={{ color: "var(--red-light)", marginTop: 4 }}>{errors.url}</div>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Description</span>
              <span style={{ color: (form.description || "").length > 300 ? "var(--red-light)" : "var(--text-fade)" }}>
                {(form.description || "").length}/300
              </span>
            </label>
            <textarea
              data-testid="resource-description-input"
              className="lq-textarea"
              rows={3}
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              maxLength={300}
            />
            {errors.description && <div className="data-line" style={{ color: "var(--red-light)" }}>{errors.description}</div>}
          </div>

          <div className="settings-grid-2" style={{ marginBottom: 14 }}>
            <div>
              <label className="label">Category</label>
              <select
                data-testid="resource-category-select"
                className="lq-select"
                value={form.category || "pro"}
                onChange={(e) => set("category", e.target.value)}
              >
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Display Order (0-999)</label>
              <input
                type="number"
                min="0"
                max="999"
                className="lq-input"
                value={form.display_order ?? 0}
                onChange={(e) => set("display_order", e.target.value)}
              />
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-mute)" }}>
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              style={{ accentColor: "var(--accent)" }}
              data-testid="resource-active-toggle"
            />
            Active — visible on the public resources page
          </label>
        </div>

        <div style={{
          padding: "14px 24px", background: "var(--bg-elev)",
          borderTop: "1px solid var(--border-soft)",
          display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button className="btn btn-ghost" onClick={onClose} data-testid="resource-cancel-btn">Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={busy} data-testid="resource-save-btn">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
